/**
 * SuperAdminBUEntitlements — Business Unit licensing controls for the
 * selected tenant. Rendered inside SuperAdminFeatures beneath the
 * Login Landing Page card.
 *
 * Responsibilities:
 *   - Live status (licensed / used / available / unlimited flag)
 *   - Manual Grant dialog (fires /super/tenants/:id/business-units/grant)
 *   - Generate Activation Code dialog with Copy + optional Send Email
 *   - Activation-code history table (revoke ACTIVE codes)
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { KeyRound, Plus, Copy, Ban, Building2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

export default function SuperAdminBUEntitlements({ tenantId, tenantName }) {
  const { getHeaders, API } = useAuth();
  const [status, setStatus] = useState(null);
  const [codes, setCodes] = useState([]);
  const [grantOpen, setGrantOpen] = useState(false);
  const [codeOpen, setCodeOpen] = useState(false);
  const [activateOpen, setActivateOpen] = useState(false);
  const [grantForm, setGrantForm] = useState({ units: 1, payment_reference: '', internal_note: '' });
  const [activateForm, setActivateForm] = useState({ initial_limit: 0, payment_reference: '', internal_note: '' });
  const [codeForm, setCodeForm] = useState({
    units: 1, expires_at: '', payment_reference: '', internal_note: '',
    send_email: false, email_recipient: '',
  });
  const [saving, setSaving] = useState(false);
  const [generatedCode, setGeneratedCode] = useState(null);

  const load = useCallback(async () => {
    if (!tenantId) return;
    try {
      const [s, c] = await Promise.all([
        axios.get(`${API}/super/tenants/${tenantId}/business-units`,
                  { headers: getHeaders() }),
        axios.get(`${API}/super/tenants/${tenantId}/activation-codes`,
                  { headers: getHeaders() }),
      ]);
      setStatus(s.data);
      setCodes(c.data.codes || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load entitlements');
    }
  }, [API, getHeaders, tenantId]);

  useEffect(() => { load(); }, [load]);

  const submitGrant = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/super/tenants/${tenantId}/business-units/grant`,
                       grantForm, { headers: getHeaders() });
      toast.success(`+${grantForm.units} Business Unit(s) granted`);
      setGrantOpen(false);
      setGrantForm({ units: 1, payment_reference: '', internal_note: '' });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Grant failed');
    } finally { setSaving(false); }
  };

  const submitActivateLicensing = async () => {
    if (activateForm.initial_limit < (status?.used || 0)) {
      toast.error(`Initial limit cannot be below current active outlets (${status?.used})`);
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API}/super/tenants/${tenantId}/business-units/activate-licensing`,
        activateForm, { headers: getHeaders() },
      );
      toast.success(`Licensing enabled — ${activateForm.initial_limit} Business Unit(s) authorised`);
      setActivateOpen(false);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Activation failed'));
    } finally { setSaving(false); }
  };

  const submitCode = async () => {
    setSaving(true);
    try {
      const r = await axios.post(`${API}/super/tenants/${tenantId}/activation-codes`,
                                  codeForm, { headers: getHeaders() });
      setGeneratedCode(r.data);
      if (r.data.email_status === 'sent') toast.success('Code generated & emailed');
      else if (r.data.email_status?.startsWith('failed'))
        toast.warning('Code generated — email delivery failed. Copy the code now.');
      else toast.success('Code generated');
      setCodeOpen(false);
      setCodeForm({
        units: 1, expires_at: '', payment_reference: '', internal_note: '',
        send_email: false, email_recipient: '',
      });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Code generation failed');
    } finally { setSaving(false); }
  };

  const revoke = async (codeId) => {
    if (!window.confirm('Revoke this activation code? This cannot be undone.')) return;
    try {
      await axios.post(`${API}/super/tenants/${tenantId}/activation-codes/${codeId}/revoke`,
                       {}, { headers: getHeaders() });
      toast.success('Code revoked');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Revoke failed');
    }
  };

  if (!status) return null;

  return (
    <>
      <Card className="border-[#E2E8DF] bg-white shadow-sm" data-testid="bu-entitlements-card">
        <CardContent className="p-4 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-md bg-amber-500/10 text-amber-700 flex items-center justify-center">
              <Building2 className="w-4 h-4" strokeWidth={2} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C6B64] font-semibold">
                Business Unit Entitlements
              </div>
              <div className="text-[13px] text-[#1A2421] font-medium truncate">
                {tenantName}
              </div>
            </div>
            <div className="flex gap-2">
              {status.licensing_enabled ? (
                <>
                  <Button size="sm" variant="outline" onClick={() => setGrantOpen(true)}
                          data-testid="bu-grant-open">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Grant
                  </Button>
                  <Button size="sm" onClick={() => setCodeOpen(true)}
                          className="bg-amber-500 hover:bg-amber-600 text-white"
                          data-testid="bu-code-open">
                    <KeyRound className="w-3.5 h-3.5 mr-1" /> Generate Code
                  </Button>
                </>
              ) : (
                <Button size="sm"
                        onClick={() => {
                          setActivateForm({
                            initial_limit: status.used || 1,
                            payment_reference: '', internal_note: '',
                          });
                          setActivateOpen(true);
                        }}
                        className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white"
                        data-testid="bu-activate-licensing-open">
                  <KeyRound className="w-3.5 h-3.5 mr-1" /> Enable Business Unit Licensing
                </Button>
              )}
            </div>
          </div>

          {status.licensing_enabled ? (
            <div className="grid grid-cols-3 gap-2 text-center">
              <StatBox label="Licensed" value={status.licensed_limit}
                       testid="bu-stat-licensed" />
              <StatBox label="Used"     value={status.used} testid="bu-stat-used" />
              <StatBox label="Available" value={status.available}
                       testid="bu-stat-available" />
            </div>
          ) : (
            <div className="rounded-md border border-dashed border-emerald-300 bg-emerald-50 p-3"
                 data-testid="bu-unlimited-panel">
              <div className="text-[10px] uppercase tracking-widest text-emerald-800 font-semibold">
                Status
              </div>
              <div className="text-sm font-medium text-emerald-900">Unlimited / Legacy</div>
              <div className="text-xs text-emerald-800/80 mt-1">
                Active Business Units:{' '}
                <span className="font-semibold" data-testid="bu-unlimited-active">
                  {status.used}
                </span>
                {' '}(server-derived active outlet count)
              </div>
              <div className="text-[11px] text-emerald-700 mt-2">
                This tenant is not under Business Unit licensing. Outlet creation is unrestricted.
                Enable licensing to place this tenant under BU entitlement.
              </div>
            </div>
          )}

          {codes.length > 0 && (
            <div className="border-t pt-3">
              <div className="text-[10px] uppercase tracking-widest text-[#5C6B64] font-semibold mb-2">
                Activation Codes
              </div>
              <div className="max-h-64 overflow-y-auto space-y-1.5">
                {codes.map((c) => (
                  <div key={c.id} className="flex items-center gap-2 text-xs border rounded p-2"
                       data-testid={`bu-code-row-${c.id}`}>
                    <code className="font-mono">
                      {c.code_display_prefix || 'RP-BU'}-••••-{c.code_suffix || '????'}
                    </code>
                    <Badge variant="outline" className={
                      c.status === 'ACTIVE'   ? 'text-emerald-700 border-emerald-300 bg-emerald-50' :
                      c.status === 'REDEEMED' ? 'text-slate-700 border-slate-300 bg-slate-50' :
                      c.status === 'EXPIRED'  ? 'text-amber-700 border-amber-300 bg-amber-50' :
                                                'text-rose-700 border-rose-300 bg-rose-50'
                    }>
                      {c.status}
                    </Badge>
                    <span className="text-slate-500">+{c.units_granted} unit(s)</span>
                    <span className="ml-auto text-slate-400 text-[11px]">
                      {String(c.created_at || '').slice(0, 10)}
                      {c.expires_at ? ` · expires ${String(c.expires_at).slice(0, 10)}` : ''}
                    </span>
                    {c.status === 'ACTIVE' && (
                      <Button size="sm" variant="ghost" className="h-6 px-2"
                              onClick={() => revoke(c.id)}
                              data-testid={`bu-code-revoke-${c.id}`}>
                        <Ban className="w-3 h-3 text-rose-500" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Activate Licensing (legacy → licensed conversion) */}
      <Dialog open={activateOpen} onOpenChange={setActivateOpen}>
        <DialogContent data-testid="bu-activate-licensing-dialog">
          <DialogHeader>
            <DialogTitle>Enable Business Unit Licensing</DialogTitle>
            <DialogDescription>
              This will place this tenant under Business Unit licensing.
              Existing outlets remain active. Future outlets above the licensed limit
              will require additional entitlement.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md bg-slate-50 border border-slate-200 p-2">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
                Current Active Business Units
              </div>
              <div className="text-xl font-semibold text-slate-800"
                   data-testid="bu-activate-current-active">
                {status?.used ?? 0}
              </div>
            </div>
            <div>
              <Label className="text-xs">Initial Licensed Business Units *</Label>
              <Input type="number"
                     min={status?.used || 1}
                     max={100000}
                     value={activateForm.initial_limit}
                     onChange={e => setActivateForm(f => ({ ...f, initial_limit: Number(e.target.value) || 0 }))}
                     data-testid="bu-activate-initial-limit" />
              <div className="text-[11px] text-slate-500 mt-1">
                Must be greater than or equal to the current active outlet count ({status?.used ?? 0}).
              </div>
            </div>
            <div>
              <Label className="text-xs">Commercial / Payment Reference (optional)</Label>
              <Input value={activateForm.payment_reference}
                     onChange={e => setActivateForm(f => ({ ...f, payment_reference: e.target.value }))}
                     placeholder="e.g. Contract ABC-2026"
                     data-testid="bu-activate-payref" />
            </div>
            <div>
              <Label className="text-xs">Internal Note (optional)</Label>
              <Textarea rows={2} value={activateForm.internal_note}
                        onChange={e => setActivateForm(f => ({ ...f, internal_note: e.target.value }))}
                        data-testid="bu-activate-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setActivateOpen(false)}>Cancel</Button>
            <Button onClick={submitActivateLicensing} disabled={saving}
                    data-testid="bu-activate-submit">
              {saving ? 'Enabling…' : 'Enable Licensing'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Grant */}
      <Dialog open={grantOpen} onOpenChange={setGrantOpen}>
        <DialogContent data-testid="bu-grant-dialog">
          <DialogHeader>
            <DialogTitle>Grant Business Units</DialogTitle>
            <DialogDescription>
              Directly increases the tenant&apos;s licensed BU limit — no activation code.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Units *</Label>
              <Input type="number" min={1} max={100} value={grantForm.units}
                     onChange={e => setGrantForm(f => ({ ...f, units: Number(e.target.value) || 1 }))}
                     data-testid="bu-grant-units" />
            </div>
            <div>
              <Label className="text-xs">Payment Reference</Label>
              <Input value={grantForm.payment_reference}
                     onChange={e => setGrantForm(f => ({ ...f, payment_reference: e.target.value }))}
                     placeholder="e.g. NEFT-483828" data-testid="bu-grant-payref" />
            </div>
            <div>
              <Label className="text-xs">Internal Note</Label>
              <Textarea rows={2} value={grantForm.internal_note}
                        onChange={e => setGrantForm(f => ({ ...f, internal_note: e.target.value }))}
                        data-testid="bu-grant-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setGrantOpen(false)}>Cancel</Button>
            <Button onClick={submitGrant} disabled={saving} data-testid="bu-grant-submit">
              {saving ? 'Granting…' : 'Grant Units'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generate Code */}
      <Dialog open={codeOpen} onOpenChange={setCodeOpen}>
        <DialogContent data-testid="bu-code-dialog">
          <DialogHeader>
            <DialogTitle>Generate Activation Code</DialogTitle>
            <DialogDescription>
              Single-use, tenant-bound. Plaintext is shown once and never stored.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Units *</Label>
                <Input type="number" min={1} max={100} value={codeForm.units}
                       onChange={e => setCodeForm(f => ({ ...f, units: Number(e.target.value) || 1 }))}
                       data-testid="bu-code-units" />
              </div>
              <div>
                <Label className="text-xs">Expiry (optional)</Label>
                <Input type="date" value={codeForm.expires_at}
                       onChange={e => setCodeForm(f => ({ ...f, expires_at: e.target.value }))}
                       data-testid="bu-code-expires" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Payment Reference</Label>
              <Input value={codeForm.payment_reference}
                     onChange={e => setCodeForm(f => ({ ...f, payment_reference: e.target.value }))}
                     data-testid="bu-code-payref" />
            </div>
            <div>
              <Label className="text-xs">Internal Note</Label>
              <Textarea rows={2} value={codeForm.internal_note}
                        onChange={e => setCodeForm(f => ({ ...f, internal_note: e.target.value }))}
                        data-testid="bu-code-note" />
            </div>
            <div className="flex items-center gap-2 pt-1">
              <input type="checkbox" checked={codeForm.send_email}
                     onChange={e => setCodeForm(f => ({ ...f, send_email: e.target.checked }))}
                     data-testid="bu-code-send-email" />
              <Label className="text-xs">Send activation email to tenant admin</Label>
            </div>
            {codeForm.send_email && (
              <Input placeholder="Override recipient (leave blank for default admin email)"
                     value={codeForm.email_recipient}
                     onChange={e => setCodeForm(f => ({ ...f, email_recipient: e.target.value }))}
                     data-testid="bu-code-email-recipient" />
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCodeOpen(false)}>Cancel</Button>
            <Button onClick={submitCode} disabled={saving} data-testid="bu-code-submit"
                    className="bg-amber-500 hover:bg-amber-600 text-white">
              {saving ? 'Generating…' : (codeForm.send_email ? 'Generate & Email' : 'Generate Only')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* One-time plaintext display */}
      <Dialog open={!!generatedCode} onOpenChange={(o) => { if (!o) setGeneratedCode(null); }}>
        <DialogContent data-testid="bu-code-display-dialog">
          <DialogHeader>
            <DialogTitle>Copy the activation code now</DialogTitle>
            <DialogDescription>
              This is the ONLY time the plaintext code will be shown.
              After this dialog closes, the plaintext is unrecoverable.
            </DialogDescription>
          </DialogHeader>
          {generatedCode && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-amber-50 border border-amber-300 text-amber-900 rounded px-3 py-2 font-mono text-sm break-all"
                      data-testid="bu-code-plaintext">
                  {generatedCode.activation_code}
                </code>
                <Button size="sm" variant="outline"
                        onClick={() => { navigator.clipboard.writeText(generatedCode.activation_code); toast.success('Copied'); }}
                        data-testid="bu-code-copy">
                  <Copy className="w-3.5 h-3.5" />
                </Button>
              </div>
              <div className="text-xs text-slate-600">
                Units: +{generatedCode.code_record?.units_granted}
                {generatedCode.code_record?.expires_at
                  ? ` · Valid until ${String(generatedCode.code_record.expires_at).slice(0, 10)}` : ''}
                {generatedCode.email_status?.startsWith('sent')
                  ? ` · ✓ Emailed`
                  : generatedCode.email_status?.startsWith('failed')
                    ? ` · ⚠ Email delivery failed`
                    : ''}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setGeneratedCode(null)} data-testid="bu-code-display-close">
              I&apos;ve copied the code
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function StatBox({ label, value, testid }) {
  return (
    <div className="border rounded p-2" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="text-xl font-semibold text-[#1A2421]">{value}</div>
    </div>
  );
}
